import { useCallback, useEffect, useRef, useState } from "react";
import type { BarcodeFormat, DetectedBarcode } from "./barcode-detector";

/**
 * Default formats the scanner accepts. Covers the four label families that
 * Sage's asset tags realistically use: Code 128 (most label printers'
 * default), Code 39 (legacy still in circulation), QR (newer programs),
 * and Data Matrix (very small labels). All other formats are intentionally
 * excluded so the detector doesn't waste cycles or accidentally lock onto
 * a UPC barcode on a nearby coffee cup.
 */
export const DEFAULT_BARCODE_FORMATS: readonly BarcodeFormat[] = [
    "code_128",
    "code_39",
    "qr_code",
    "data_matrix",
];

/**
 * Per-frame decoder cadence. Real-world phones can decode a 1080p frame in
 * 30–80ms; ten frames a second is plenty for a barcode-scanner UX (the
 * user perceives sub-200ms detection as "instant") and dramatically less
 * battery-hungry than the rAF firehose.
 */
const DECODE_INTERVAL_MS = 100;

export interface BarcodeScanResult {
    readonly value: string;
    readonly format: BarcodeFormat;
}

export type BarcodeScannerStatus =
    | "idle"
    | "starting"
    | "scanning"
    | "permission-denied"
    | "no-camera"
    | "unsupported"
    | "error";

export interface UseBarcodeScannerOptions {
    /** Scoped formats. Defaults to {@link DEFAULT_BARCODE_FORMATS}. */
    readonly formats?: readonly BarcodeFormat[];
    /** Called when the first centered barcode is decoded. The caller is responsible for stopping the scanner if it doesn't want any more results. */
    readonly onResult: (result: BarcodeScanResult) => void;
}

export interface UseBarcodeScannerReturn {
    /** Bind to a `<video autoPlay playsInline muted />` element. */
    readonly videoRef: React.RefObject<HTMLVideoElement>;
    readonly status: BarcodeScannerStatus;
    /** Human-friendly message; null in `idle`/`scanning`. */
    readonly message: string | null;
    /** Begin the camera + decode loop. Safe to call repeatedly. */
    readonly start: () => Promise<void>;
    /** Stop tracks + decode loop. Safe to call repeatedly. */
    readonly stop: () => void;
}

/**
 * Live-camera barcode scanner. Field-agnostic — the caller supplies an
 * `onResult` callback and decides what to do with the decoded value.
 *
 * Lifecycle responsibilities:
 *   - Acquires `getUserMedia({ video: { facingMode: "environment" } })`.
 *   - Runs a poll-based decode loop (NOT requestAnimationFrame — see
 *     {@link DECODE_INTERVAL_MS}). Skips re-entry if a prior `detect()`
 *     is still in flight.
 *   - Picks the **centermost** barcode when multiple are in frame; this
 *     matches user intent better than "highest-confidence" or "first
 *     returned" and avoids accidentally locking onto a sticker at the
 *     edge of the viewport.
 *   - Auto-pauses when the document hides (tab switch, app background)
 *     and resumes on visibility-change. Phones suspend MediaStream tracks
 *     unpredictably otherwise.
 *   - Tears down on unmount: tracks stopped, timer cleared, video src
 *     unset. Critical — leaked tracks keep the camera "on" indicator lit
 *     even after the user navigates away.
 *
 * Status surface keeps callers from needing to interpret raw DOMException
 * names: each known failure maps to a stable string the UI can switch on.
 */
export function useBarcodeScanner(
    options: UseBarcodeScannerOptions,
): UseBarcodeScannerReturn {
    const { formats, onResult } = options;
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const detectorRef = useRef<BarcodeDetector | null>(null);
    const decodeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Re-entrancy guard: a slow `detect()` shouldn't pile up calls behind
    // it. Tracked in a ref because closures over component state would lag.
    const detectInFlightRef = useRef(false);
    const stoppedRef = useRef(false);
    // Stable callback: lets `start` close over the latest handler without
    // each `onResult` change tearing down the camera stream.
    const onResultRef = useRef(onResult);
    onResultRef.current = onResult;

    const [status, setStatus] = useState<BarcodeScannerStatus>("idle");
    const [message, setMessage] = useState<string | null>(null);

    const stop = useCallback(() => {
        stoppedRef.current = true;
        if (decodeTimerRef.current !== null) {
            clearTimeout(decodeTimerRef.current);
            decodeTimerRef.current = null;
        }
        const stream = streamRef.current;
        if (stream !== null) {
            for (const track of stream.getTracks()) {
                track.stop();
            }
            streamRef.current = null;
        }
        const video = videoRef.current;
        if (video !== null) {
            video.srcObject = null;
        }
        detectInFlightRef.current = false;
        setStatus("idle");
        setMessage(null);
    }, []);

    const start = useCallback(async (): Promise<void> => {
        if (!isBarcodeScannerSupported()) {
            setStatus("unsupported");
            setMessage("Barcode scanning isn't supported in this browser.");
            return;
        }
        if (streamRef.current !== null) {
            // Already running. Don't tear down — just leave it alone.
            return;
        }

        stoppedRef.current = false;
        setStatus("starting");
        setMessage(null);

        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" } },
                audio: false,
            });
        } catch (err) {
            const code = errorName(err);
            if (code === "NotAllowedError" || code === "SecurityError") {
                setStatus("permission-denied");
                setMessage(
                    "Camera permission was denied. Allow camera access in your browser to scan barcodes.",
                );
            } else if (code === "NotFoundError" || code === "OverconstrainedError") {
                setStatus("no-camera");
                setMessage("No usable camera was found on this device.");
            } else {
                setStatus("error");
                setMessage(
                    `Camera could not be started — ${err instanceof Error ? err.message : "unknown error"}.`,
                );
            }
            return;
        }

        if (stoppedRef.current) {
            // Caller invoked stop() while permission prompt was open.
            for (const track of stream.getTracks()) track.stop();
            return;
        }

        streamRef.current = stream;
        try {
            // Construct lazily; some browsers throw at construction time
            // when an unsupported format is requested. Catching here lets
            // us fall back to the default format set rather than crashing.
            detectorRef.current = new BarcodeDetector({
                formats: (formats ?? DEFAULT_BARCODE_FORMATS).slice(),
            });
        } catch {
            detectorRef.current = new BarcodeDetector();
        }

        const video = videoRef.current;
        if (video === null) {
            // The component using us hasn't bound the ref yet (or has
            // unmounted). Treat as "stopped" so we don't leak the stream.
            stop();
            return;
        }
        video.srcObject = stream;
        video.muted = true;
        video.playsInline = true;
        try {
            await video.play();
        } catch {
            // Autoplay can fail on some browsers if the element wasn't
            // muted in time; second `play()` after we set `muted = true`
            // usually succeeds on the next loop, but if it doesn't, we
            // still proceed — the decode loop reads pixels from the
            // backing track regardless of UI playback state.
        }

        setStatus("scanning");
        // Decode loop is defined inside start() so it closes over no React
        // state — only refs and the stable callback ref above. Mutual
        // recursion via function-declaration hoisting (NOT const arrow)
        // lets scheduleNextDecode forward-reference runDecode.
        function scheduleNextDecode(): void {
            if (decodeTimerRef.current !== null) {
                clearTimeout(decodeTimerRef.current);
            }
            decodeTimerRef.current = setTimeout(() => {
                void runDecode();
            }, DECODE_INTERVAL_MS);
        }

        async function runDecode(): Promise<void> {
            if (stoppedRef.current) return;
            if (detectInFlightRef.current) {
                scheduleNextDecode();
                return;
            }
            const video = videoRef.current;
            const detector = detectorRef.current;
            if (video === null || detector === null) {
                scheduleNextDecode();
                return;
            }
            // HAVE_ENOUGH_DATA = 4. Decoding before the first frame is
            // decoded wastes a call and some browsers throw
            // InvalidStateError.
            if (video.readyState < 2) {
                scheduleNextDecode();
                return;
            }

            detectInFlightRef.current = true;
            try {
                const results = await detector.detect(video);
                detectInFlightRef.current = false;
                if (stoppedRef.current) return;
                if (results.length > 0) {
                    const best = pickCentermost(results, video);
                    onResultRef.current({ value: best.rawValue, format: best.format });
                    // Stop scheduling further decodes; the caller will
                    // call stop() when it processes the result. Keeping
                    // the stream alive lets the UI render a "captured"
                    // frame freeze if it wants.
                    return;
                }
            } catch {
                // Per-frame decode failures are common and transient
                // (e.g. partial frame, motion blur). Drop the result and
                // keep looping; surfacing every failure would be noise.
                detectInFlightRef.current = false;
            }
            scheduleNextDecode();
        }

        scheduleNextDecode();
    }, [formats, stop]);

    // Handle tab/app suspension: pause the stream when the page hides so
    // the camera indicator goes away, and resume on return. Without this,
    // some Android browsers leave the stream "frozen" and detect() returns
    // the same stale frame indefinitely after the user comes back.
    useEffect(() => {
        function handleVisibility(): void {
            if (document.hidden) {
                if (streamRef.current !== null) {
                    stop();
                }
            }
        }
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, [stop]);

    // Unmount cleanup. Belt-and-suspenders against parents that forget to
    // call stop() before tearing down the sheet.
    useEffect(() => {
        return () => {
            stop();
        };
    }, [stop]);

    return {
        videoRef: videoRef as React.RefObject<HTMLVideoElement>,
        status,
        message,
        start,
        stop,
    };
}

/**
 * Browser feature probe. Cheap enough to call at render time; consumers
 * (e.g. the trailing-icon button) use it to decide whether to render at
 * all rather than expose a non-functional control.
 */
export function isBarcodeScannerSupported(): boolean {
    return (
        typeof window !== "undefined" &&
        "BarcodeDetector" in window &&
        typeof navigator !== "undefined" &&
        navigator.mediaDevices !== undefined &&
        typeof navigator.mediaDevices.getUserMedia === "function"
    );
}

function pickCentermost(
    barcodes: readonly DetectedBarcode[],
    video: HTMLVideoElement,
): DetectedBarcode {
    // detect() always returns >=1 here; pickCentermost is only called
    // after the empty-array check in runDecode. The non-null assertion
    // would be safer with `!` but we use a guard for clarity.
    const first = barcodes[0];
    if (first === undefined) {
        throw new Error("pickCentermost called with empty array");
    }
    if (barcodes.length === 1) return first;

    const cx = video.videoWidth / 2;
    const cy = video.videoHeight / 2;
    let bestDistance = Number.POSITIVE_INFINITY;
    let best: DetectedBarcode = first;
    for (const b of barcodes) {
        const bx = b.boundingBox.x + b.boundingBox.width / 2;
        const by = b.boundingBox.y + b.boundingBox.height / 2;
        const d = Math.hypot(bx - cx, by - cy);
        if (d < bestDistance) {
            bestDistance = d;
            best = b;
        }
    }
    return best;
}

function errorName(err: unknown): string | null {
    if (err instanceof Error) return err.name;
    return null;
}
